// Customer journeys: Salesforce's own record of how each person at a company entered. History's
// last section; its deals are drawn by HistoryTab.
//
// A journey belongs to a *person*, not to a company. Eleven journeys on one account are eleven
// different people, and rendered without the name they would show as eleven identical lines and
// read as a duplication bug. `CONTACT_C` is populated on all 317 journeys on the book -- which
// makes this block, not the deals, where "who do I talk to here" is answerable.
//
// Nothing in here says outbound produced any of this. `category` is Salesforce's own label and
// reads "Cold Outbound" on most of them; whether this dashboard's activity is what created a
// journey categorised that way is an open attribution question, and a caption here would
// quietly answer it.

import type { Journey } from '../api/types';
import { CardRows, CardTitle, HoverCard } from '../components/HoverCard';
import { contactDisplay } from '../lib/contactName';
import { CollapsibleList } from './Collapsible';

// How far along a journey is, as a tone rather than a rank. There is a real order --
// Identified, Approached, Engaged, Accepted, Converted, Closed -- but the block only needs to
// separate "still moving" from "finished", so this stops short of implying a score.
const JOURNEY_TONE: Record<string, string> = {
  Converted: 'live', Accepted: 'live', Engaged: 'live', Closed: 'done',
};

/** Every Salesforce field, labelled with the name Salesforce gives it, for the hover card. */
function journeyDetailRows(journey: Journey): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  if (journey.email) rows.push(['Email', journey.email]);
  if (journey.title) rows.push(['Title', journey.title]);
  if (journey.type) rows.push(['Type', journey.type]);
  if (journey.category) rows.push(['Category', journey.category]);
  if (journey.status) rows.push(['Status', journey.status]);
  if (journey.created) rows.push(['Created', journey.created]);
  // Salesforce records the sequence a journey came from in the qualification reason, and
  // replaces it with a close reason once the journey ends. Labelled as whichever it is.
  if (journey.close_reason) rows.push(['Closed', journey.close_reason]);
  else if (journey.qualification_reason) rows.push(['Sequence', journey.qualification_reason]);
  return rows;
}

// One journey in the opportunity cards' compact shape: who and status on top, type and
// category under it, the day on the right, and every other field on hover.
function JourneyRow({ journey }: { journey: Journey }) {
  // 31 of the book's 317 journeys have no contact name -- the same playground-signup
  // placeholders the People block handles -- so the name is read off a first.last address, else
  // the address identifies the person. It has to: one account has two journeys for two
  // different unnamed people, and without it they render as one row printed twice.
  const display = contactDisplay(journey.who, journey.email, journey.type || 'Unnamed in Salesforce');
  const meta = [journey.type, journey.category].filter(Boolean).join(' · ');
  return (
    <div className="dealrow journeyrow">
      <HoverCard className="dealnametrigger" label={`About ${display.text}'s journey`} card={() => (
        <>
          <CardTitle>{display.text}</CardTitle>
          <CardRows rows={journeyDetailRows(journey)} />
        </>
      )}>
        <span className={'dealname' + (display.named ? '' : ' unnamed')}>{display.text}</span>
      </HoverCard>
      <span className={'jst ' + (JOURNEY_TONE[journey.status ?? ''] ?? '')}>{journey.status || 'No status'}</span>
      <span className="dealmeta">{meta || journey.title || ''}</span>
      <span className="dealdate">{journey.created || ''}</span>
    </div>
  );
}

export function journeysBlock(journeys: Journey[]) {
  // 162 of the 314 accounts have no journey at all. That is a real absence rather than a thin
  // read, and the block disappears rather than announcing it.
  if (!journeys.length) return { body: null, hint: null };

  // The count is people, not records, because that is what a reader will take it for. They
  // differ: one account holds two journeys for the same person. Identity keys on the email
  // where there is one, since a placeholder name is not an identity.
  const people = new Set(
    journeys.map(journey => journey.email || journey.who).filter(Boolean),
  );
  const unidentified = journeys.filter(journey => !journey.email && !journey.who).length;
  const peopleCount = people.size + unidentified;

  return {
    body: (
      <CollapsibleList
        collapseKey="journeys"
        items={journeys}
        plural="journeys"
        renderRow={(journey, index) => <JourneyRow journey={journey} key={index} />}
      />
    ),
    hint: `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}, newest first`,
  };
}
